/* eslint-disable lingui/no-unlocalized-strings -- endereço e verbo HTTP, não texto de UI */
import axios, { type AxiosInstance } from 'axios';
import { z } from 'zod';

import { startSpan } from '@/shared/telemetry';

import { DEFAULT_TIMEOUT_MS } from './apis';

/**
 * ViaCEP (discovery/01 §6.2, chamada #16): fora das cinco APIs, sem token nem tenant, como o
 * `queryZipCode` do legado (`services/zipCode`, `fetch` cru). Serve a todo formulário com endereço.
 */
export const VIA_CEP_ORIGIN = 'https://viacep.com.br';

export const viaCepResponseSchema = z.looseObject({
  cep: z.string().optional(),
  logradouro: z.string().nullish(),
  complemento: z.string().nullish(),
  bairro: z.string().nullish(),
  localidade: z.string().nullish(),
  uf: z.string().nullish(),
  erro: z.union([z.boolean(), z.string()]).optional(),
});

/** Endereço de um CEP, com os nomes do formulário do legado. */
export interface CepAddress {
  readonly addressName: string;
  readonly district: string;
  readonly city: string;
  readonly uf: string;
}

let instance: AxiosInstance | undefined;
const client = () => (instance ??= axios.create({ baseURL: `${VIA_CEP_ORIGIN}/ws`, timeout: DEFAULT_TIMEOUT_MS, responseType: 'json' }));

/**
 * `GET https://viacep.com.br/ws/{cep}/json/` com o CEP cru (8 dígitos). `null` quando o ViaCEP diz
 * `erro: true` (CEP inexistente); falha de rede rejeita.
 */
export async function lookupCep(cep: string, signal?: AbortSignal): Promise<CepAddress | null> {
  const span = startSpan('http', 'GET /ws/{cep}/json', { 'uyzy.api': 'ViaCep', 'url.template': '/ws/{cep}/json', 'http.request.method': 'GET' }, { client: true });
  try {
    const response = await client().get<unknown>(`/${cep}/json/`, signal ? { signal } : {});
    span.end({ 'http.response.status_code': response.status });
    const parsed = viaCepResponseSchema.safeParse(response.data);
    if (!parsed.success) {
      if (!import.meta.env.PROD) console.warn('GET ViaCEP fora do contrato', parsed.error.issues);
      return null;
    }
    const data = parsed.data;
    if (data.erro === true || data.erro === 'true') return null;
    return { addressName: data.logradouro ?? '', district: data.bairro ?? '', city: data.localidade ?? '', uf: data.uf ?? '' };
  } catch (error) {
    span.end({ 'error.type': axios.isAxiosError(error) ? (error.code ?? 'network') : 'unknown' }, { code: 'error' });
    throw error;
  }
}
